import { PatientWithInsurance } from '@dataTypes/appointment.type'
import {
  BillingPaymentNotePayload,
  InvoiceEntry,
  PaymentEntry,
  PaymentType,
  AmountType,
} from '@dataTypes/billing.types'
import { GetAllPatients } from '@services/api/patient.api'
import {
  clearSelectedInvoices,
  fetchARAging,
  fetchInsuranceARAnalytics,
  fetchPatientARAnalytics,
  fetchPaymentHistoryEntries,
  recordPayment,
  removeInvoiceFromSelection,
  setPaymentPagination,
} from '@store/actions/billing.action'
import { AppDispatch, RootState } from '@store/index'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import {
  listPaymentMethods,
  mapPaymentMethodApiToDisplay,
  type SavedCardDisplay,
} from '@services/api/billing/payment-method.api'
import { createCardPaymentEntry } from '@services/api/billing/payment.api'
import { usePaymentStatus } from '@hooks/usePaymentStatus'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { v4 as uuidv4 } from 'uuid'
import { buildPaymentFilters } from '../filterBuilders'
import { logger } from '@utils/safeLogger'

interface UsePaymentHooks {
  selectedInvoices: InvoiceEntry[]
  paymentHistory: PaymentEntry[]
  paymentHistoryCount: number
  loadingPaymentHistory: boolean
  historySearchQuery: string
  historyFilters: Record<string, string[]>
  filteredPayments: InvoiceEntry[]
  filteredHistory: PaymentEntry[]
  setHistorySearchQuery: (query: string) => void
  setHistoryFilters: (filters: Record<string, string[]>) => void
  handleHistorySearchChange: (value: string) => void
  handleHistoryFilterChange: (newFilters: Record<string, string[]>) => void
  currentPage: number
  pageSize: number
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handlePaymentHistorySortChange: (sort: string) => void
  handlePaymentHistoryTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
  /** Removes an invoice from payment selection (local Redux only; thunk has no I/O). */
  handleRemoveFromSelection: (invoiceNo: string) => void
  // PaymentDetailsLayout hooks
  paymentDetails: {
    method: PaymentType
    setMethod: (method: PaymentType) => void
    date: string
    amountType: AmountType
    setAmountType: (type: AmountType) => void
    amount: number
    setAmount: (amount: number) => void
    amountDraft: string | null
    payor: string
    setPayor: (payor: string) => void
    notes: BillingPaymentNotePayload[]
    setNotes: (notes: BillingPaymentNotePayload[]) => void
    allPatients: PatientWithInsurance[]
    loadingPatients: boolean
    payorSelectOptions: Array<{ value: string; label: string }>
    selectedWithPaid: Array<
      InvoiceEntry & { amountPaid: number; amountDue: number }
    >
    totalAmountDue: number
    remainingAmount: number
    isFullyPaid: boolean
    selectedPayorOption: { value: string; label: string } | undefined
    handleUpdateNotes: (data: BillingPaymentNotePayload[]) => void
    handleSubmit: () => void
    handleAmountTypeChangeFull: () => void
    handleAmountTypeChangePartial: () => void
    handleAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleAmountBlur: (e: React.FocusEvent<HTMLInputElement>) => void
    isAmountInvalid: boolean
    // Card-specific
    savedCards: SavedCardDisplay[]
    loadingSavedCards: boolean
    selectedPaymentMethodId: string | null
    setSelectedPaymentMethodId: (id: string | null) => void
    isCardProcessing: boolean
  }
}

const PAYMENT_HISTORY_SEARCH_DEBOUNCE_MS = 300

const normalizeBillingPaymentNotes = (
  sourceNotes: BillingPaymentNotePayload[],
): BillingPaymentNotePayload[] => {
  return sourceNotes.reduce<BillingPaymentNotePayload[]>((acc, note) => {
    const rawBody =
      typeof note.body === 'string'
        ? note.body
        : typeof note.content === 'string'
          ? note.content
          : ''
    const normalizedBody = rawBody.trim()
    if (!normalizedBody) {
      return acc
    }

    acc.push({
      ...note,
      body: normalizedBody,
      content:
        typeof note.content === 'string' && note.content.trim().length > 0
          ? note.content
          : normalizedBody,
    })
    return acc
  }, [])
}

const usePayment = (
  showToast?: ReturnType<typeof useNotificationToast>['showToast'],
  options?: { suppressTableLoading?: boolean },
): UsePaymentHooks => {
  const dispatch = useDispatch<AppDispatch>()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const suppressTableLoadingRef = useRef(options?.suppressTableLoading)
  const isInitialListFetchRef = useRef(true)
  suppressTableLoadingRef.current = options?.suppressTableLoading
  const {
    selectedInvoices,
    paymentHistory,
    paymentHistoryCount,
    loadingPaymentHistory,
    paymentPagination,
  } = useSelector((state: RootState) => state.billing)
  const { currentPage, pageSize, sort } = paymentPagination
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  // PaymentTable state
  const [historySearchQuery, setHistorySearchQuery] = useState<string>('')
  const debouncedHistorySearchQuery = useDebouncedValue(
    historySearchQuery,
    PAYMENT_HISTORY_SEARCH_DEBOUNCE_MS,
  )
  const [historyFilters, setHistoryFilters] = useState<
    Record<string, string[]>
  >({})

  // PaymentDetailsLayout state
  const [method, setMethod] = useState<PaymentType>(PaymentType.CASH)
  const [date, setDate] = useState<string>(
    () => new Date().toISOString().split('T')[0],
  )
  const [amountType, setAmountType] = useState<AmountType>(AmountType.Full)
  const [amount, setAmount] = useState<number>(0)
  /** In-progress amount text; null means show the committed `amount` number. */
  const [amountDraft, setAmountDraft] = useState<string | null>(null)
  const [payor, setPayor] = useState<string>('')
  const [notes, setNotes] = useState<BillingPaymentNotePayload[]>([])
  const notesRef = useRef<BillingPaymentNotePayload[]>([])
  const [allPatients, setAllPatients] = useState<PatientWithInsurance[]>([])
  const [loadingPatients, setLoadingPatients] = useState<boolean>(false)

  const selectedPatient = useSelector(
    (state: RootState) => state.patient.selectedPatient,
  )
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )

  // Card payment state
  const [savedCards, setSavedCards] = useState<SavedCardDisplay[]>([])
  const [loadingSavedCards, setLoadingSavedCards] = useState(false)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null)
  const [cardPollingKey, setCardPollingKey] = useState<string | null>(null)
  const [isCardProcessing, setIsCardProcessing] = useState(false)
  const idempotencyKeyRef = useRef<string | null>(null)

  const {
    status: cardPaymentStatus,
    stripePayment: cardStripePayment,
    error: cardPollError,
    stopPolling: stopCardPolling,
  } = usePaymentStatus(cardPollingKey)

  const resetCardPaymentState = useCallback((): void => {
    stopCardPolling()
    setIsCardProcessing(false)
    setCardPollingKey(null)
    idempotencyKeyRef.current = null
  }, [stopCardPolling])

  // Fetch saved cards when method is Card and payor is selected
  useEffect(() => {
    if (method !== PaymentType.CARD || !payor || !selectedClinic?.clinic_id) {
      setSavedCards([])
      setSelectedPaymentMethodId(null)
      return
    }

    let cancelled = false
    setLoadingSavedCards(true)

    listPaymentMethods(payor, selectedClinic.clinic_id)
      .then((res) => {
        if (cancelled) return
        const methods = (res.data?.payment_methods ?? []).map(
          mapPaymentMethodApiToDisplay,
        )
        setSavedCards(methods)
        const defaultCard = methods.find((c) => c.isDefault) ?? methods[0]
        setSelectedPaymentMethodId(defaultCard?.payment_method_id ?? null)
      })
      .catch(() => {
        if (cancelled) return
        setSavedCards([])
      })
      .finally(() => {
        if (!cancelled) setLoadingSavedCards(false)
      })

    return () => {
      cancelled = true
    }
  }, [method, payor, selectedClinic?.clinic_id])

  // Handle card payment polling result
  useEffect(() => {
    if (!cardPollingKey) return

    if (cardPaymentStatus === 'succeeded') {
      hideLoading('.billing-page')
      resetCardPaymentState()
      // Refresh data
      dispatch(setPaymentPagination({ currentPage: 1 }))
      dispatch(clearSelectedInvoices())
      setMethod(PaymentType.CASH)
      setAmountType(AmountType.Full)
      setAmount(0)
      setPayor('')
      notesRef.current = []
      setNotes([])
      dispatch(fetchPaymentHistoryEntries())
      dispatch(fetchPatientARAnalytics())
      dispatch(fetchInsuranceARAnalytics())
      dispatch(fetchARAging())
      showToast?.(
        createSuccessToast(
          'Card payment successful',
          'Payment has been processed and confirmed.',
        ),
      )
    } else if (cardPaymentStatus === 'failed') {
      hideLoading('.billing-page')
      resetCardPaymentState()
      showToast?.(
        createFailedToast(
          'Card payment failed',
          cardStripePayment?.failure_reason ??
            'Payment was declined. Please try again.',
        ),
      )
    }

    if (cardPollError) {
      hideLoading('.billing-page')
      resetCardPaymentState()
      showToast?.(createFailedToast('Payment error', cardPollError))
    }
  }, [
    cardPaymentStatus,
    cardStripePayment,
    cardPollError,
    cardPollingKey,
    resetCardPaymentState,
    dispatch,
    currentPage,
    pageSize,
    showToast,
    hideLoading,
  ])

  // Fetch patients for PaymentDetailsLayout
  useEffect(() => {
    let isMounted = true

    const fetchPatientsForDetails = async (): Promise<void> => {
      setLoadingPatients(true)
      try {
        const response = await GetAllPatients()
        if (
          isMounted &&
          response.statusCode === 200 &&
          response.data.patients
        ) {
          setAllPatients(response.data.patients)
        }
      } catch (error) {
        logger.error('[PaymentDetailsLayout] Error fetching patients:', error)
        showToast?.(
          createFailedToast(
            'Patients',
            error instanceof Error ? error.message : 'Failed to fetch patients',
          ),
        )
      } finally {
        if (isMounted) {
          setLoadingPatients(false)
        }
      }
    }

    fetchPatientsForDetails()

    return () => {
      isMounted = false
    }
  }, [selectedClinic?.clinic_id, showToast])

  useEffect(() => {
    dispatch(setPaymentPagination({ currentPage: 1 }))
  }, [
    dispatch,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    debouncedHistorySearchQuery,
    historyFilters.method,
  ])

  useEffect(() => {
    const fetchPaymentHistory = async (): Promise<void> => {
      const queryFilters = buildPaymentFilters({
        search: debouncedHistorySearchQuery,
        tabFilters: {
          method: historyFilters.method ?? [],
        },
        sort,
      })

      const suppressOverlay =
        suppressTableLoadingRef.current || isInitialListFetchRef.current
      if (!suppressOverlay) {
        showLoading({ container: '.payment-history-table' })
      }
      try {
        await dispatch(fetchPaymentHistoryEntries(queryFilters)).unwrap()
      } finally {
        isInitialListFetchRef.current = false
        hideLoading('.payment-history-table')
      }
    }
    void fetchPaymentHistory()
  }, [
    dispatch,
    showLoading,
    hideLoading,
    currentPage,
    pageSize,
    sort,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    debouncedHistorySearchQuery,
    historyFilters.method,
  ])

  // PaymentTable handlers
  const handleHistorySearchChange = useCallback((value: string): void => {
    setHistorySearchQuery(value)
  }, [])

  const handleHistoryFilterChange = useCallback(
    (newFilters: Record<string, string[]>): void => {
      setHistoryFilters(newFilters)
    },
    [],
  )

  const handlePageChange = useCallback(
    (page: number): void => {
      dispatch(setPaymentPagination({ currentPage: page }))
    },
    [dispatch],
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number): void => {
      dispatch(setPaymentPagination({ pageSize: newPageSize, currentPage: 1 }))
    },
    [dispatch],
  )

  const handlePaymentHistorySortChange = useCallback(
    (newSort: string): void => {
      dispatch(setPaymentPagination({ sort: newSort, currentPage: 1 }))
    },
    [dispatch],
  )

  const handlePaymentHistoryTableSortChange = useCallback(
    ({ key, direction }: { key: string; direction: SortDirection }): void => {
      if (key === 'paymentDate') {
        handlePaymentHistorySortChange(`paymentDate:${direction}`)
      }
      if (key === 'method') {
        handlePaymentHistorySortChange(`method:${direction}`)
      }
      if (key === 'paidBy' || key === 'payer') {
        handlePaymentHistorySortChange(`payer:${direction}`)
      }
    },
    [handlePaymentHistorySortChange],
  )

  // PaymentDetailsLayout computed values
  const selectedWithPaid = useMemo(() => {
    return selectedInvoices.map((inv) => {
      const amountPaid =
        Number(inv.patientResponsibilityTotal) -
        Number(inv.patientResponsibilityBalance)
      const amountDue = Number(inv.patientResponsibilityBalance) || 0
      return {
        ...inv,
        amountPaid: Number(amountPaid) || 0,
        amountDue: Number(amountDue) || 0,
      }
    })
  }, [selectedInvoices])

  const payorSelectOptions = useMemo(() => {
    return allPatients
      .map((pat) => {
        const fullName = `${pat.first_name || ''} ${pat.last_name || ''}`.trim()
        if (fullName && pat.patient_id) {
          return {
            value: pat.patient_id,
            label: fullName,
          }
        }
        return null
      })
      .filter((opt): opt is { value: string; label: string } => opt !== null)
  }, [allPatients])

  const totalAmountDue = useMemo(
    () =>
      selectedWithPaid.reduce(
        (sum, inv) => sum + (isNaN(inv.amountDue) ? 0 : inv.amountDue),
        0,
      ),
    [selectedWithPaid],
  )

  const remainingAmount = useMemo(
    () =>
      selectedWithPaid.reduce(
        (sum, inv) => sum + (isNaN(inv.amountDue) ? 0 : inv.amountDue),
        0,
      ),
    [selectedWithPaid],
  )

  const isFullyPaid = totalAmountDue === 0

  const selectedPayorOption = useMemo(
    () => payorSelectOptions.find((opt) => opt.value === payor),
    [payor, payorSelectOptions],
  )

  // PaymentDetailsLayout handlers
  const handleUpdateNotes = useCallback(
    (data: BillingPaymentNotePayload[]): void => {
      notesRef.current = data
      setNotes(data)
    },
    [],
  )

  const commitAmountFromText = useCallback(
    (raw: string): number => {
      const value = parseFloat(raw.replace(/,/g, '')) || 0
      const roundedValue = Math.round(value * 100) / 100
      const cappedValue =
        amountType === AmountType.Partial && roundedValue > totalAmountDue
          ? Math.round(totalAmountDue * 100) / 100
          : roundedValue
      setAmount(cappedValue)
      setAmountDraft(null)
      return cappedValue
    },
    [amountType, totalAmountDue],
  )

  const handleSubmit = useCallback((): void => {
    const paymentAmount =
      amountType === AmountType.Partial
        ? amountDraft !== null
          ? commitAmountFromText(amountDraft)
          : amount
        : remainingAmount
    const normalizedNotes = normalizeBillingPaymentNotes(notesRef.current)

    const invoiceIds = selectedWithPaid.map((invoice) => invoice.invoiceNo)

    const referenceNumber =
      normalizedNotes.length > 0
        ? normalizedNotes
            .map((note) => {
              const content =
                typeof note?.body === 'string' ? note.body : note?.content
              return typeof content === 'string' ? content : ''
            })
            .filter((content): content is string => {
              return typeof content === 'string' && content.trim().length > 0
            })
            .join('; ') || undefined
        : undefined

    if (!selectedClinic?.clinic_id) return

    // Card payment — initiate via Stripe
    if (method === PaymentType.CARD) {
      if (!selectedPaymentMethodId || isCardProcessing) return

      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = uuidv4()
      }

      setIsCardProcessing(true)
      showLoading({ container: '.billing-page' })
      createCardPaymentEntry({
        clinic_id: selectedClinic.clinic_id,
        amount: paymentAmount,
        invoice_ids: invoiceIds,
        paid_by: payor,
        created_by: user.user_id,
        payment_method_id: selectedPaymentMethodId,
        idempotency_key: idempotencyKeyRef.current,
        ...(referenceNumber && {
          reference_number: referenceNumber,
        }),
        ...(normalizedNotes.length > 0 && { notes: normalizedNotes }),
      })
        .then(() => {
          setCardPollingKey(idempotencyKeyRef.current)
        })
        .catch((err: unknown) => {
          hideLoading('.billing-page')
          setIsCardProcessing(false)
          idempotencyKeyRef.current = null
          showToast?.(
            createFailedToast(
              'Payment not initiated',
              err instanceof Error ? err.message : 'Please try again.',
            ),
          )
        })
      return
    }

    // Cash (and other non-card) payment
    showLoading({ container: '.billing-page' })
    dispatch(
      recordPayment({
        invoiceIds,
        amountPaid: paymentAmount,
        paidBy: payor,
        createdBy: user.user_id,
        method,
        referenceNumber,
        clinicId: selectedClinic?.clinic_id,
        ...(normalizedNotes.length > 0 && { notes: normalizedNotes }),
      }),
    )
      .unwrap()
      .then(async () => {
        hideLoading('.billing-page')
        dispatch(setPaymentPagination({ currentPage: 1 }))
        dispatch(clearSelectedInvoices())
        setMethod(PaymentType.CASH)
        setDate('')
        setAmountType(AmountType.Full)
        setAmount(0)
        setAmountDraft(null)
        setPayor('')
        notesRef.current = []
        setNotes([])
        dispatch(fetchPaymentHistoryEntries())
        dispatch(fetchPatientARAnalytics())
        dispatch(fetchInsuranceARAnalytics())
        dispatch(fetchARAging())
        if (showToast) {
          showToast(
            createSuccessToast(
              'Payment recorded successfully',
              'Payment has been recorded successfully.',
            ),
          )
        }
      })
      .catch(() => {
        hideLoading('.billing-page')
        if (showToast) {
          showToast(
            createFailedToast(
              'Payment not recorded',
              'Payment not recorded. Please try again.',
            ),
          )
        }
      })
  }, [
    amountType,
    amount,
    amountDraft,
    commitAmountFromText,
    remainingAmount,
    selectedWithPaid,
    dispatch,
    payor,
    user.user_id,
    method,
    selectedClinic?.clinic_id,
    selectedPaymentMethodId,
    isCardProcessing,
    showToast,
    showLoading,
    hideLoading,
  ])

  // Update amount when selected invoices change
  useEffect(() => {
    if (selectedWithPaid.length > 0) {
      setAmountType(AmountType.Full)
      setAmount(parseFloat(remainingAmount.toFixed(2)))
      setAmountDraft(null)
    } else {
      setAmountType(AmountType.Full)
      setAmount(0)
      setAmountDraft(null)
      setPayor('')
    }
  }, [selectedWithPaid, remainingAmount])

  // Automatically set amount to totalAmountDue when amountType is AmountType.Full
  useEffect(() => {
    if (amountType === AmountType.Full) {
      const roundedAmount = Math.round(totalAmountDue * 100) / 100
      setAmount(roundedAmount)
      setAmountDraft(null)
    } else if (
      amountType === AmountType.Partial &&
      amountDraft === null &&
      amount > totalAmountDue
    ) {
      // Cap partial payment amount at totalAmountDue (committed value only)
      const cappedAmount = Math.round(totalAmountDue * 100) / 100
      setAmount(cappedAmount)
    }
  }, [amountType, totalAmountDue, amount, amountDraft])

  // PaymentDetailsLayout amount handlers
  const handleAmountTypeChangeFull = useCallback((): void => {
    setAmountType(AmountType.Full)
    const roundedAmount = Math.round(totalAmountDue * 100) / 100
    setAmount(roundedAmount)
    setAmountDraft(null)
  }, [totalAmountDue])

  const handleAmountTypeChangePartial = useCallback((): void => {
    setAmountType(AmountType.Partial)
  }, [])

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setAmountDraft(e.target.value.replace(/,/g, ''))
    },
    [],
  )

  const handleAmountBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>): void => {
      commitAmountFromText(e.target.value)
    },
    [commitAmountFromText],
  )

  const isAmountInvalid = useMemo(() => {
    if (amountType !== AmountType.Partial) {
      return false
    }
    if (amountDraft !== null) {
      const draftValue = parseFloat(amountDraft.replace(/,/g, ''))
      return !isNaN(draftValue) && draftValue > totalAmountDue
    }
    return amount > totalAmountDue
  }, [amountType, amount, amountDraft, totalAmountDue])

  // PaymentTable computed values
  const filteredPayments = useMemo(() => {
    return selectedInvoices.map((invoice) => {
      const totalPaid = paymentHistory
        .filter((ph) => ph.invoiceId === invoice.invoiceNo)
        .reduce((sum, ph) => sum + ph.amountPaid, 0)

      return {
        ...invoice,
        amountPaid: totalPaid,
      }
    })
  }, [selectedInvoices, paymentHistory])

  const filteredHistory = useMemo(() => paymentHistory, [paymentHistory])

  const handleRemoveFromSelection = useCallback(
    (invoiceNo: string): void => {
      void dispatch(removeInvoiceFromSelection(invoiceNo))
        .unwrap()
        .then(() => {
          showToast?.(
            createSuccessToast(
              'Invoice removed from selection',
              'Invoice has been removed from the payment selection.',
            ),
          )
        })
        .catch(() => {
          showToast?.(
            createFailedToast(
              'Failed to remove invoice',
              'Invoice could not be removed from selection. Please try again.',
            ),
          )
        })
    },
    [dispatch, showToast],
  )

  return {
    selectedInvoices,
    paymentHistory,
    paymentHistoryCount,
    loadingPaymentHistory,
    historySearchQuery,
    historyFilters,
    filteredPayments,
    filteredHistory,
    setHistorySearchQuery,
    setHistoryFilters,
    handleHistorySearchChange,
    handleHistoryFilterChange,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handlePaymentHistorySortChange,
    handlePaymentHistoryTableSortChange,
    handleRemoveFromSelection,
    paymentDetails: {
      method,
      setMethod,
      date,
      amountType,
      setAmountType,
      amount,
      setAmount,
      amountDraft,
      payor,
      setPayor,
      notes,
      setNotes,
      allPatients,
      loadingPatients,
      payorSelectOptions,
      selectedWithPaid,
      totalAmountDue,
      remainingAmount,
      isFullyPaid,
      selectedPayorOption,
      handleUpdateNotes,
      handleSubmit,
      handleAmountTypeChangeFull,
      handleAmountTypeChangePartial,
      handleAmountChange,
      handleAmountBlur,
      isAmountInvalid,
      savedCards,
      loadingSavedCards,
      selectedPaymentMethodId,
      setSelectedPaymentMethodId,
      isCardProcessing,
    },
  }
}

export default usePayment
